import { Booking, Expense, ServiceType, ExpenseCategory } from '../types';

export interface StreamMargin {
  type: ServiceType;
  income: number;
  expense: number;
  margin: number;
}

export type MarginStatus = 'healthy' | 'caution' | 'danger';

export interface MarginInsightItem {
  id: string;
  type: 'alert' | 'caution' | 'info';
  text: string;
  metric?: string;
}

/**
 * Calculates overall profit margin percentage (excluding cancelled invoices)
 */
export function calculateMargin(bookings: Booking[], expenses: Expense[]): number {
  const activeBookings = (bookings || []).filter(b => b.status !== 'cancelled');
  const totalIncome = activeBookings.reduce((sum, b) => sum + (Number(b.price) || 0), 0);
  const totalExpenses = (expenses || []).reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
  
  if (totalIncome === 0) {
    return totalExpenses > 0 ? -100 : 100; // -100% margin if pure expenses, 100% if no activity
  }
  
  const profit = totalIncome - totalExpenses;
  return (profit / totalIncome) * 100;
}

/**
 * Returns status level and styling info based on current margin and threshold
 */
export function getMarginStatus(margin: number, threshold: number): {
  status: MarginStatus;
  label: string;
  badgeClass: string;
  bgClass: string;
  borderClass: string;
  textColor: string;
  glowColor: string;
} {
  if (margin < threshold) {
    return {
      status: 'danger',
      label: 'Action Required',
      badgeClass: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
      bgClass: 'bg-rose-950/10',
      borderClass: 'border-rose-500/20',
      textColor: 'text-rose-400',
      glowColor: 'rgba(244, 63, 94, 0.15)'
    };
  }
  
  // Caution buffer: within 5% points above the threshold (e.g. if threshold is 48, caution is 48% to 53%)
  if (margin >= threshold && margin <= threshold + 5) {
    return {
      status: 'caution',
      label: 'Caution / Monitor',
      badgeClass: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
      bgClass: 'bg-amber-950/10',
      borderClass: 'border-amber-500/20',
      textColor: 'text-amber-400',
      glowColor: 'rgba(245, 158, 11, 0.15)'
    };
  }
  
  return {
    status: 'healthy',
    label: 'Healthy / Efficient',
    badgeClass: 'bg-[#14B8A6]/10 text-[#14B8A6] border-[#14B8A6]/20',
    bgClass: 'bg-[#14B8A6]/5',
    borderClass: 'border-[#14B8A6]/20',
    textColor: 'text-[#14B8A6]',
    glowColor: 'rgba(20, 184, 166, 0.15)'
  };
}

/**
 * Calculates individual margins for each of the service streams (excluding cancelled invoices)
 */
export function getStreamMargins(bookings: Booking[], expenses: Expense[], activeStreams?: ServiceType[]): StreamMargin[] {
  const activeBookings = (bookings || []).filter(b => b.status !== 'cancelled');
  // Combine custom activeStreams with any types found in active bookings/expenses, removing duplicates
  const allStreams = Array.from(new Set([
    ...(activeStreams || []),
    ...activeBookings.map(b => b.type),
    ...(expenses || []).map(e => e.linked_stream)
  ])).filter(Boolean);
  
  return allStreams.map(type => {
    const income = activeBookings
      .filter(b => b.type === type)
      .reduce((sum, b) => sum + (Number(b.price) || 0), 0);
      
    const expense = (expenses || [])
      .filter(e => e.linked_stream === type)
      .reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
      
    const margin = income > 0 ? ((income - expense) / income) * 100 : (expense > 0 ? -100 : 100);
    
    return {
      type,
      income,
      expense,
      margin
    };
  });
}

/**
 * Analyzes current metrics to generate built-in financial insights (NOT advice)
 */
export function generateMarginInsights(
  bookings: Booking[],
  expenses: Expense[],
  threshold: number,
  activeStreams?: ServiceType[]
): MarginInsightItem[] {
  const insights: MarginInsightItem[] = [];
  const activeBookings = (bookings || []).filter(b => b.status !== 'cancelled');
  const totalIncome = activeBookings.reduce((sum, b) => sum + (Number(b.price) || 0), 0);
  const totalExpenses = (expenses || []).reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
  const overallMargin = calculateMargin(bookings, expenses);
  
  // 1. Overall margin vs threshold check
  if (overallMargin < threshold) {
    insights.push({
      id: 'ins-overall-below',
      type: 'alert',
      text: `Operating below target margin: Your current profitability (${overallMargin.toFixed(1)}%) is under your safe threshold zone (${threshold}%).`,
      metric: 'Action Needed'
    });
  }

  // 2. High Category expense analysis
  if (totalExpenses > 0) {
    const categories: ExpenseCategory[] = ['salary', 'travel', 'supplies', 'rent', 'other'];
    const categoryTotals = categories.map(cat => {
      const sum = expenses.filter(e => e.category === cat).reduce((total, e) => total + e.amount, 0);
      const percentage = (sum / totalExpenses) * 100;
      return { cat, sum, percentage };
    });
    
    // Sort descending to find highest spending category
    categoryTotals.sort((a, b) => b.sum - a.sum);
    const topCat = categoryTotals[0];
    
    if (topCat && topCat.percentage >= 30) {
      const catLabels: Record<ExpenseCategory, string> = {
        salary: 'Salary & Payroll',
        travel: 'Travel',
        supplies: 'Supplies',
        rent: 'Rent',
        other: 'Other operations'
      };
      insights.push({
        id: `ins-cat-high-${topCat.cat}`,
        type: topCat.percentage > 50 ? 'alert' : 'caution',
        text: `Margin decreased due to high ${catLabels[topCat.cat]} spending, which constitutes ${topCat.percentage.toFixed(0)}% of all operations.`,
        metric: `R${topCat.sum.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
      });
    }
  }

  // 3. Stream-specific margin checks
  if (totalIncome > 0) {
    const streams = getStreamMargins(bookings, expenses, activeStreams);
    streams.forEach(stream => {
      const labelMap: Record<ServiceType, string> = {
        consult: 'Consult stream',
        retreat: 'Retreat stream',
        coffee: 'Coffee stream'
      };
      
      const label = labelMap[stream.type] || `"${stream.type}" stream`;
      
      // Only report if they actually have activity in this stream
      if (stream.income > 0) {
        if (stream.margin < threshold) {
          insights.push({
            id: `ins-stream-below-${stream.type}`,
            type: 'alert',
            text: `${label} margin (${stream.margin.toFixed(0)}%) dropped below target threshold of ${threshold}%.`,
            metric: `${stream.margin.toFixed(0)}% margin`
          });
        } else if (stream.margin < threshold + 10) {
          insights.push({
            id: `ins-stream-warning-${stream.type}`,
            type: 'caution',
            text: `${label} margin is hovering near threshold at ${stream.margin.toFixed(0)}%.`,
            metric: 'Near Limit'
          });
        }
      }
    });
  }

  // 4. Overspending check
  if (totalExpenses > totalIncome && totalIncome > 0) {
    insights.push({
      id: 'ins-deficit',
      type: 'alert',
      text: `Expenses exceeded income bookings: Operating at a net loss of R${(totalExpenses - totalIncome).toLocaleString()} in this scope.`,
      metric: 'Operating Loss'
    });
  }

  // Fallback default safe-check if everything is perfect
  if (insights.length === 0) {
    insights.push({
      id: 'ins-perfect-health',
      type: 'info',
      text: 'All operational streams are well-balanced and margins currently exceed your target. Keep running lean!',
      metric: 'Optimal'
    });
  }

  return insights;
}
